import React from 'react';
import { Minus, Plus, Trash } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { getItemLineTotal } from '../../utils/cartMath';

const CartItem = ({ item, index }) => {
  const { updateQuantity, removeItem, inventoryMap = {}, cartItems = [] } = useCart();

  const productId = item.productId || item.id;
  const currentStock = inventoryMap?.[productId]?.currentStock || 0;

  const otherSameItemsInCart = cartItems.reduce((total, cartItem, idx) => {
    if (!cartItem || idx === index) return total;
    const itemProductId = cartItem.productId || cartItem.id;
    return itemProductId === productId ? total + cartItem.quantity : total;
  }, 0);

  const maxQuantity = Math.max(0, currentStock - otherSameItemsInCart);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value > 0) {
      updateQuantity(index, Math.min(value, maxQuantity || 99));
    }
  };

  const hasPackagingCharge = item.packagingCharge && item.packagingCharge > 0;

  return (
    <div className="flex flex-col py-4 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>

          {item.customizations && item.customizations.length > 0 && (
            <div className="text-xs text-gray-500 mt-1 space-y-0.5">
              {item.customizations.map((customization, i) => (
                <div key={`${customization.value || i}`} className="flex justify-between items-center">
                  <span>{customization.optionLabel}</span>
                  <span className="text-amber-600 ml-2">+₹{customization.priceModifier.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {hasPackagingCharge && (
            <div className="text-xs text-gray-400 mt-1">Packaging: ₹{item.packagingCharge.toFixed(2)}</div>
          )}

          {maxQuantity > 0 && maxQuantity < item.quantity && (
            <div className="text-xs text-red-500 mt-1">Only {maxQuantity} available now.</div>
          )}
        </div>

        <div className="text-right shrink-0">
          <div className="font-medium text-sm text-gray-900">₹{getItemLineTotal(item).toFixed(2)}</div>
          <button
            className="flex items-center justify-center w-11 h-11 -mr-3 -mt-1 text-gray-400 hover:text-red-500 transition-colors"
            onClick={() => removeItem(index)}
            aria-label="Remove item"
          >
            <Trash size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex items-center mt-3">
        <div className="flex items-center border border-gray-200 rounded-full h-11">
          <button
            className="h-full w-11 flex items-center justify-center text-gray-500 disabled:opacity-30"
            onClick={() => updateQuantity(index, item.quantity - 1)}
            disabled={item.quantity <= 1}
            aria-label="Decrease quantity"
          >
            <Minus size={14} weight="bold" />
          </button>
          <input
            type="number"
            min="1"
            max={maxQuantity || 99}
            value={item.quantity}
            onChange={handleQuantityChange}
            className="w-9 text-center text-sm bg-transparent focus:outline-none"
          />
          <button
            className="h-full w-11 flex items-center justify-center text-gray-500 disabled:opacity-30"
            onClick={() => updateQuantity(index, item.quantity + 1)}
            disabled={maxQuantity > 0 && item.quantity >= maxQuantity}
            aria-label="Increase quantity"
          >
            <Plus size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
